import { createContext, useState, useCallback, useMemo, useContext as useContext$1, Suspense, lazy, useRef, useEffect } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { classNames as classNames$1 } from '@uds/typography';
import { cx } from 'linaria';
import './erk.stylesheet_12x39tw.css';
import './action.stylesheet_mlnsdp.css';
import './smirk.stylesheet_1g3izz6.css';
import { generate } from 'shortid';

const Context = /*#__PURE__*/createContext(undefined);
const ContextProvider = Context.Provider;
const Provider = _ref => {
  let {
    children
  } = _ref;
  const [queue, setQueue] = useState([]);
  const add = useCallback(item => {
    setQueue(queue => queue.concat(item));
  }, []);
  const patch = useCallback(queueItem => {
    setQueue(queue => queue.map(item => {
      if (item.id === queueItem.id) {
        return {
          ...item,
          ...queueItem
        };
      }
      return item;
    }));
  }, []);
  const remove = useCallback(id => {
    setQueue(queue => queue.filter(item => item.id !== id));
  }, []);
  const expire = useCallback((id, transitionOut) => {
    patch({
      id,
      expired: true
    });
    setTimeout(() => {
      remove(id);
    }, transitionOut);
  }, [patch, remove]);
  const value = useMemo(() => ({
    queue,
    add,
    expire
  }), [queue, add, expire]);
  return /*#__PURE__*/jsx(ContextProvider, {
    value: value,
    children: children
  });
};
const useContext = () => {
  const context = useContext$1(Context);
  if (context === undefined) {
    throw new Error('useContext must be used within a ContextProvider');
  }
  return context;
};

var useAdd = (() => {
  const {
    add
  } = useContext();
  return useCallback(item => add({
    id: generate(),
    expired: false,
    ...item
  }), [add]);
});

const DURATION_IN = 300;
const DURATION_OUT_RELAXED = 600;
const DURATION_OUT_PENDING = 300;
const EXPIRE_RELAXED = 6000;
const EXPIRE_PENDING = 4000;
let KnowledgeType;
(function (KnowledgeType) {
  KnowledgeType["SMIRK"] = "SMIRK";
  KnowledgeType["ERK"] = "ERK";
})(KnowledgeType || (KnowledgeType = {}));

var useErk = (() => {
  const add = useAdd();
  return smirk => add({
    type: KnowledgeType.ERK,
    ...smirk
  });
});

var useSmirk = (() => {
  const add = useAdd();
  return smirk => add({
    type: KnowledgeType.SMIRK,
    ...smirk
  });
});

const ReactiveKnowledge = /*#__PURE__*/lazy(() => import('./ReactiveKnowledge-01c1c20a.js'));
function Entry(_ref) {
  let {
    children
  } = _ref;
  return /*#__PURE__*/jsxs(Provider, {
    children: [children, /*#__PURE__*/jsx(Suspense, {
      fallback: /*#__PURE__*/jsx("div", {}),
      children: /*#__PURE__*/jsx(ReactiveKnowledge, {})
    })]
  });
}

const root$2 = "r250qon";
const compactClass$1 = "c1n43vrw";
const wrapper$1 = "w1icvwgr";
const message$1 = "myjsj5l";
const action = "a1fpt43l";
const useClassNames$1 = _ref => {
  let {
    compact,
    hasAction
  } = _ref;
  return {
    root: cx(root$2, compact && compactClass$1, classNames$1.footnote),
    wrapper: wrapper$1,
    message: cx(message$1, hasAction && action)
  };
};

const root$1 = "r1yu7fje";
const classNames = {
  root: root$1
};

const ErkAction = _ref => {
  let {
    label,
    onRetry
  } = _ref;
  const ref = useRef();
  useEffect(() => {
    const actionButton = ref.current;
    if (actionButton != null) {
      // We should focus this button, so those navigating by keyboard
      // have a chance to click the button before it umounts.
      actionButton.focus();
    }
  }, []);
  return /*#__PURE__*/jsx("button", {
    ref: ref,
    type: "button",
    className: classNames.root,
    onClick: onRetry,
    children: label
  });
};
var Action = ErkAction;

const PureErk = _ref => {
  let {
    label,
    children,
    onRetry,
    compact
  } = _ref;
  const hasAction = onRetry != null && label != null;
  const classNames = useClassNames$1({
    hasAction,
    compact
  });
  return /*#__PURE__*/jsx("div", {
    className: classNames.root,
    children: /*#__PURE__*/jsxs("div", {
      className: classNames.wrapper,
      children: [/*#__PURE__*/jsx("div", {
        className: classNames.message,
        role: "alert",
        children: children
      }, "message"), hasAction && /*#__PURE__*/jsx(Action, {
        label: label,
        onRetry: onRetry
      })]
    })
  });
};
var Erk = PureErk;

const root = "r1j0auxn";
const compactClass = "cd2qqhg";
const wrapper = "w1qekq7h";
const message = "m1tkwg7h";
const useClassNames = _ref => {
  let {
    compact
  } = _ref;
  return {
    root: cx(root, compact && compactClass, classNames$1.footnote),
    wrapper,
    message
  };
};

const PureSmirk = _ref => {
  let {
    compact,
    children
  } = _ref;
  const classNames = useClassNames({
    compact
  });
  return /*#__PURE__*/jsx("div", {
    className: classNames.root,
    children: /*#__PURE__*/jsx("div", {
      className: classNames.wrapper,
      children: /*#__PURE__*/jsx("div", {
        className: classNames.message,
        role: "alert",
        children: children
      })
    })
  });
};
var Smirk = PureSmirk;

function ReactiveKnowledgeProvider(_ref) {
  let {
    onAdd,
    onErk,
    onSmirk,
    children
  } = _ref;
  const value = useMemo(() => ({
    queue: [],
    add: item => {
      onAdd === null || onAdd === void 0 ? void 0 : onAdd(item);
      if (item.type === KnowledgeType.SMIRK) {
        onSmirk === null || onSmirk === void 0 ? void 0 : onSmirk(item);
      } else {
        onErk === null || onErk === void 0 ? void 0 : onErk(item);
      }
    },
    expire: () => undefined
  }), [onAdd, onErk, onSmirk]);
  return /*#__PURE__*/jsx(Context.Provider, {
    value: value,
    children: children
  });
}

export { Context as C, DURATION_IN as D, Erk as E, KnowledgeType as K, ReactiveKnowledgeProvider as R, Smirk as S, DURATION_OUT_RELAXED as a, DURATION_OUT_PENDING as b, EXPIRE_PENDING as c, EXPIRE_RELAXED as d, useAdd as e, useErk as f, useSmirk as g, Entry as h, useContext as u };
